
import { Link } from "react-router-dom";

import React, { useEffect, useState } from "react";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { getLocations } from "@/api/Services/locations";
import { getCategories } from "@/api/Services/categories";
import { getJobList, Job } from "@/api/Services/joblist";

const FeaturedJobs = () => {
  const [categories, setCategories] = useState<string[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [role, setRole] = useState<string>("");
  const [category, setCategory] = useState<string>("all");
  const [location, setLocation] = useState<string>("all");
  const [jobs, setJobs] = useState<Job[]>([]);

useEffect(() => {
  const fetchData = async () => {
    const cats = await getCategories();
    const locs = await getLocations();
    setCategories(cats);
    setLocations(locs);

    // Fetch jobs without filters initially
    const initialJobs = await getJobList({
      limit: 5,
      page: 1,
      orderBy: "updatedAt",
      orderDirection: "ASC",
      status: "ACTIVE",
    });
    setJobs(initialJobs);
  };
  fetchData();
}, []);


  const handleSearch = async (e: React.FormEvent) => {
  e.preventDefault();

  const filteredJobs = await getJobList({
    role,
    description: role, // optional, can be more detailed
    category: category === "all" ? "" : category,
    location: location === "all" ? "" : location,
    status: "ACTIVE",
    globalSearch: role,
    page: 1,
    limit: 5,
    orderBy: "updatedAt",
    orderDirection: "ASC",
  });

  setJobs(filteredJobs);
};


  const handleClearAll = async () => {
  setRole("");
  setCategory("all");
  setLocation("all");

  const allJobs = await getJobList({
    limit: 5,
    page: 1,
    orderBy: "updatedAt",
    orderDirection: "ASC",
    status: "ACTIVE",
  });
  setJobs(allJobs);
};


  return (
    <>
      <section className="bg-white py-10">
        <div className="text-[32px] lg:text-[48px] text-center font-bold mb-10 mt-[-10]">
          Featured Jobs
        </div>

        <div className="py-10" style={{
            background: "linear-gradient(270.95deg, #136A85 0%, #00A3D4 87.52%)",
          }}>
          <div className="px-17 max-w-7xl mx-auto">
            <form
              className="grid grid-cols-1 gap-5 lg:grid-cols-4"
              onSubmit={handleSearch}
            >
              {/* Job Role */}
              <div className="flex flex-col">
                <label className="text-gray-200 text-lg mb-2">Job Role</label>
                <input
                  type="text"
                  className="cursor-pointer border border-grey-200 px-5 py-3 rounded-full text-white text-lg focus:outline-none"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="Role"
                />
              </div>

              {/* Job Category */}
              <div className="flex flex-col">
                <label className="text-gray-200 text-lg mb-2">Job Category</label>
                <Select
                  value={category}
                  onValueChange={(val) => setCategory(val)}
                >
                  <SelectTrigger className="w-full h-13 rounded-full border border-gray-200 px-5 py-3 text-gray-200 text-lg focus:outline-none [&>span>svg]:text-gray-200 cursor-pointer">
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg shadow-lg max-h-60">
                    <SelectItem value="all">All</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location */}
              <div className="flex flex-col">
                <label className="text-gray-200 text-lg mb-2">Location</label>
                <Select
                  value={location}
                  onValueChange={(val) => setLocation(val)}
                >
                  <SelectTrigger className="w-full h-13 rounded-full border border-grey-200 px-5 py-3 text-gray-200 text-lg cursor-pointer">
                    <SelectValue placeholder="Select Location" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg shadow-lg max-h-60">
                    <SelectItem value="all">All</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc} value={loc}>
                        {loc}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <button
                  type="button"
                  className="mt-2 text-lg text-gray-200 hover:text-white self-end cursor-pointer"
                  onClick={handleClearAll}
                >
                  Clear All
                </button>
              </div>

              {/* Search Button */}
              <div className="flex flex-col">
                <label className="text-gray-200 text-lg mb-2 invisible">
                  Search
                </label>
                <button
                  type="submit"
                  className="cursor-pointer w-full h-12 bg-white text-gray-700 text-lg font-medium rounded-full"
                >
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <JobCard jobs={jobs} />
    </>
  );
};



interface JobCardProps {
  jobs: Job[];
}

const JobCard: React.FC<JobCardProps> = ({ jobs }) => {
  return (
    <section className="bg-white flex items-center justify-center mb-10">
      <div className="grid grid-cols-1 gap-6 w-full max-w-[1150px]">
        {jobs.length === 0 ? (
          <p className="text-center text-gray-500">No jobs available</p>
        ) : (
          jobs.map((job) => (
            <div
              key={job._id}
              className="rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
              style={{
                background:
                  "linear-gradient(309.45deg, rgba(0, 167, 89, 0.08) 24.42%, rgba(0, 163, 212, 0.08) 69.56%)",
              }}
            >
              <div className="flex-1">
                <h3 className="text-gray-800 font-semibold text-lg md:text-xl">
                  {job.jobName}
                </h3>
                <p className="text-gray-600 text-sm md:text-base mt-2">
                  {job.description.split(" ").slice(0, 10).join(" ") + " ....."}
                </p>
                <p className="text-gray-500 text-sm mt-1">
                  Location: {job.location.join(", ")} | Experience:{" "}
                  {job.experienceRequired.join(", ")}
                </p>
                <Link
                  to={`/career/job/${job._id}`}
                  className="text-red-500 text-sm font-medium mt-3 inline-block hover:underline"
                >
                  Know More
                </Link>
              </div>
              <Link
              to={`/career/job/${job._id}`}
              className="flex md:justify-end w-full md:w-auto"
            >
              <button
                className="
                  bg-orange-600 text-white 
                  text-xs px-4 py-3   /* small size for mobile */
                  md:text-base md:px-6 md:py-2  /* normal size for desktop */
                  font-semibold rounded-full 
                  hover:bg-orange-700 transition
                  cursor-pointer
                  w-auto md:w-auto
                  self-start md:self-auto   /* left align in mobile, normal in desktop */
                "
              >
                Apply Now
              </button>
            </Link>

            </div>
          ))
        )}
      </div>
    </section>
  );
};





export default FeaturedJobs;
